import {
  SpecialDates,
  TourDate,
  TourInclusion,
  OfferDetails,
  DatesAndPricingData,
  TourDateDisplayItem
} from "@/types/dto/pdp/pdp-dates-inclusions-pricing";

/**
 * Creates a mock API item with default values for testing
 */
export const createMockApiItem = (overrides: Partial<TourDateDisplayItem> = {}): TourDateDisplayItem => ({
  packageDateNeoId: "107485",
  packageNeoId: "16128",
  departureYear: "2025",
  tourStartTourEnd: "Sep 27 - Oct 8, 2025",
  startDate: "2025-09-27T00:00:00Z",
  endDate: "2025-10-08T00:00:00Z",
  singleStrikeThruPrice: null,
  doubleStrikeThruPrice: null,
  doublePrice: "$5,799",
  singlePrice: "$7,199",
  offerSavings: null,
  bookingUrl: "/en/tours/europe/italy/best-of-italy/booking?b=1&packagedate=123",
  callForDetails: false,
  gauranteed: false,
  soldOut: false,
  seatsRemainingBelowThreshold: false,
  seatsRemainingText: "",
  callForDetailsText: null,
  hasOffer: false,
  isHotDeal: false,
  isSeasonal: false,
  isPercentDollarOff: false,
  isFlashSale: false,
  offerDisclaimer: null,
  offerSummary: null,
  hasSpecialEvent: false,
  specialEventDescription: "",
  callForDetailsPhoneNumber: null,
  isAirIncluded: false,
  itineraryType: "Standard",
  ...overrides
});

/**
 * Derives the status message from API item flags
 * Priority: Sold Out > Call to Book > Limited Seats > Guaranteed
 */
function getStatusMessage(item: TourDateDisplayItem): string | undefined {
  if (item.soldOut) return "Sold Out";
  if (item.seatsRemainingBelowThreshold) {
    return item.seatsRemainingText || "Limited Seats";
  }
  if (item.callForDetails) return "Call to Book";
  if (item.gauranteed) return "Guaranteed";
  return undefined;
}

/**
 * Derives the offer code from API item flags
 */
function getOfferCode(item: TourDateDisplayItem): string {
  if (item.isSeasonal) return "SEASONAL";
  if (item.isHotDeal) return "HOTDEAL";
  if (item.isPercentDollarOff) return "DISCOUNT";
  if (item.isFlashSale) return "FLASHSALE";
  return "";
}

/**
 * Transforms API TourDateDisplayItem to component TourDate
 */
export const createTourDateFromApi = (item: TourDateDisplayItem): TourDate => ({
  packageId: item.packageNeoId,
  packageDateId: item.packageDateNeoId,
  startDate: item.startDate.split("T")[0],
  neoTourDate: /^\d{4}-\d{2}-\d{2}$/.test(item.neoTourDate ?? "") ? (item.neoTourDate as string) : undefined,
  endDate: item.endDate.split("T")[0],
  tourStartTourEnd: item.tourStartTourEnd,
  doublePrice: item.doublePrice,
  singlePrice: item.singlePrice,
  singleStrikeOutPrice: item.singleStrikeThruPrice || undefined,
  strikeOutPrice: item.doubleStrikeThruPrice || undefined,
  offerPrice: item.hasOffer ? item.doublePrice : undefined,
  offerCode: getOfferCode(item),
  statusMessage: getStatusMessage(item),
  callForDetailsPhoneNumber: item.callForDetailsPhoneNumber ?? undefined,
  callForDetails: item.callForDetails,
  seatsRemainingBelowThreshold: item.seatsRemainingBelowThreshold,
  statusId: item.soldOut ? 3 : 1,
  onTourAir: item.isAirIncluded,
  bookNowUrl: item.bookingUrl,
  departureYear: item.departureYear,
  specialDates: item.hasSpecialEvent
    ? [{ title: "Special Event" as const, description: item.specialEventDescription }]
    : [],
  itineraryType: item.itineraryType,
  hasOffer: item.hasOffer

});

/**
 * Extracts offer details from given API item
 */
export const getOfferDetails = (currentPackageDateId: TourDateDisplayItem | undefined): OfferDetails | undefined => {
  if (!currentPackageDateId) return undefined;
  if (!currentPackageDateId.hasOffer) return undefined;

  return {
    offerCode: getOfferCode(currentPackageDateId),
    offerTitle: currentPackageDateId.offerSavings || "",
    offerAmount: currentPackageDateId.offerSavings?.match(/\$[\d,]+/)?.[0] || "",
    offerDescription: currentPackageDateId.offerSummary || "",
    isHotDeal: currentPackageDateId.isHotDeal ?? false,
    termsAndConditions: currentPackageDateId.offerDisclaimer || ""
  };
};

/**
 * Extracts unique sorted years from API items
 */
export const getAvailableYears = (items: TourDateDisplayItem[]): string[] =>
  [...new Set(items.map(item => item.departureYear))].sort();

export const specialDatesFactory = (data: any = {}): SpecialDates => ({
  title: data?.title ?? "Special Event",
  description: data?.description ?? ""
});

export const tourDateFactory = (data: any = {}): TourDate => ({
  packageId: data?.packageId ?? "",
  packageDateId: data?.packageDateId ?? "",
  startDate: data?.startDate ?? "",
  endDate: data?.endDate ?? "",
  strikeOutPrice: data?.strikeOutPrice ?? "",
  singlePrice: data?.singlePrice ?? "",
  doublePrice: data?.doublePrice ?? "",
  specialDates: data?.specialDates?.map((offer: any) => specialDatesFactory(offer)
  ) ?? [],
  offerPrice: data?.offerPrice ?? "",
  statusMessage: data?.statusMessage ?? undefined,
  onTourAir: data?.onTourAir ?? false,
  bookNowUrl: data?.bookNowUrl ?? "",
  statusId: data?.statusId ?? 0,
  offerCode: data?.offerCode ?? "",
  departureYear: data?.departureYear ?? "",
  itineraryType: data?.itineraryType ?? "",
  hasOffer: data?.hasOffer ?? false,
  callForDetails: data?.callForDetails ?? false,
  seatsRemainingBelowThreshold: data?.seatsRemainingBelowThreshold ?? false
});

export const tourInclusionFactory = (data: any = {}): TourInclusion => ({
  id: data?.id ?? "",
  title: data?.title ?? "",
  description: data?.description ?? undefined,
  iconUrl: data?.iconUrl ?? undefined,
  tooltipText: data?.tooltipText ?? undefined,
  hasModal: data?.hasModal ?? false
});

export const offerDetailsFactory = (data: any = {}): OfferDetails => ({
  offerCode: data?.offerCode ?? "",
  offerTitle: data?.offerTitle ?? "",
  offerDescription: data?.offerDescription ?? "",
  offerAmount: data?.offerAmount ?? undefined,
  termsAndConditions: data?.termsAndConditions ?? "",
  expirationDate: data?.expirationDate ?? undefined
});

export const datesAndPricingDataFactory = (data: any = {}): DatesAndPricingData => ({
  years: data?.years ?? [],
  tourDates: data?.tourDates?.map((date: any) => tourDateFactory(date)) ?? [],
  inclusions: data?.inclusions?.map((inclusion: any) => tourInclusionFactory(inclusion)) ?? [],
  offerDetails: data?.offerDetails ? offerDetailsFactory(data.offerDetails) : undefined
});
